import React, {
  useState,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle
} from 'react';
import PropTypes from 'prop-types';
import { Canvas, useFrame, useThree, createRoot } from '@react-three/fiber';
import * as THREE from 'three';
import { DesignContext } from '../contexts/designContext.js';

import Roof from './Roof.js';
import Wall from './Wall.js';
import Vertex from './Vertex.js';
import Tree from './Tree.js';
import Ground from './Ground.js';
import Sun from './Sun.js';
import Sky from './Sky.js';
import GridHelper from './GridHelper.js';
import Controls from './Controls.js';
import ObjectSelection from './SelectionBox.js';
import Annotations from './Annotations.js';
import Drawing, { StringingDrawing } from './Drawing.js';
import CanvasShapeAnnotations from './CanvasShapeAnnotations.js';
import { buildRoofPlanes, projectAndSegmentString } from '../algorithms/geometry.js';

export const Scene = forwardRef(({ data }, forwardedRef) => {
  const {
    segments,
    obstacles,
    trees,
    annotations,
    accordion,
    gwidth,
    gheight,
    canvas,
    controls,
    sceneRef,
    viewMode,
    coordinates,
    resolution,
    rgbTexture,
    texture,
    dsmTexture,
    orgDSMTexture,
    minDSM,
    maxDSM,
    displacementBias,
    dsmOffset,
    materials,
    month,
    weather,
    setback,
    insertObject,
    readonly = false,
    selectedObjects,
    segmentSelection,
    pointSelection,
    lineSelection,
    panelSelection,
    setbackSelection,
    panelVisibility = false,
    moveStatus,
    showGround,
    showRoofs,
    showTrees,
    showSunLight,
    showSetbacks,
    showDSM,
    showPanels,
    showDSMHD,
    showDSMHouse,
    cx,
    cy,
    c,
    center,
    scale,
    ranges,
    orgVertices,
    vertices,
    vertices3D,
    verticesType,
    parents,
    UV,
    eaves,
    pitches,
    azimuths,
    eaveHeights,
    treeData,
    panelData,
    panelEaves,
    setbacksData,
    setbacks3dData,
    roofTransformations,
    getSetbacks,
    clearAllSelection,
    addToSelectedObjects,
    removeFromSelectedObjects,
    addSelectedPoint,
    removeSelectedPoint,
    moveSelection,
    movePanels,
    insertPanels,
    canInsertPanel,
    addShape,
    editShapes,
    mode,
    labels,
    changeClockwise,
    tool,
    viewState,
    iframeCallback,
    designFrame,
    panelPlacementOnlySelectedRoof,
    initialZoom = 3, // Default to 3 for backward compatibility
    onTreeClick = null, // Optional. When provided (e.g. by designer), called with (treeIndex) when a tree is clicked in 3D.
    colorSetbacksByEdgeType = false
  } = data;

  const annotationDeletePicking = mode === 'delete' && accordion === 'annotations' && viewMode === '2D';

  const roofRefs = useRef([]);

  const [stringLines, setStringLines] = useState([]);
  const showStringing = Boolean(data?.showStringing) && (accordion === 'production' || data?.forceShowStringing === true);

  const getPanelCenter = useCallback(
    (roofIdx, panelIdx) => {
      const roofVertexIndices = (data?.verticesType || [])
        .map((t, idx) => (t === 'roof' ? idx : null))
        .filter((v) => v !== null);
      const roofIdxMap = data?.stringingRoofIndexMap;
      const mappedRoofIdx = Array.isArray(roofIdxMap) ? roofIdxMap[roofIdx] : roofIdx;
      const vertexIdx = roofVertexIndices[mappedRoofIdx];
      const roofRef = vertexIdx !== undefined ? roofRefs.current?.[vertexIdx] : null;
      if (!roofRef?.getPanelCentersWorld) return null;
      const centers = roofRef.getPanelCentersWorld() || [];
      const mode = data?.stringingPanelIndexMode || 'all';
      if (mode === 'selected') {
        const mapByRoof = data?.stringingSelectedPanelIndexMap;
        const roofMap = Array.isArray(mapByRoof) ? mapByRoof[roofIdx] : null;
        if (Array.isArray(roofMap)) {
          const remapped = roofMap[panelIdx];
          return remapped !== undefined ? centers[remapped] || null : null;
        }
        const selectedIdxsRaw = data?.selectedObjects?.panels?.[vertexIdx] || [];
        const selectedIdxs = Array.isArray(selectedIdxsRaw) ? [...selectedIdxsRaw] : [];
        const remapped = selectedIdxs[panelIdx];
        return remapped !== undefined ? centers[remapped] || null : null;
      }
      return centers[panelIdx] || null;
    },
    [
      data?.verticesType,
      data?.stringingRoofIndexMap,
      data?.stringingPanelIndexMode,
      data?.stringingSelectedPanelIndexMap,
      data?.selectedObjects?.panels
    ]
  );

  useEffect(() => {
    if (!showStringing) {
      setStringLines([]);
      return;
    }
    const strings = data?.stringingData;
    if (!strings || !Array.isArray(strings)) {
      setStringLines([]);
      return;
    }

    const footprint =
      data?.footprint || data?.footprintPolygon || data?.footprint_polygon || data?.lead?.property?.footprint || null;
    const roofPlanes = buildRoofPlanes(data?.vertices3D, data?.verticesType);

    const built = strings
      .map((s) => {
        if (s?.draw === false) return null;
        const poly = s?.polyline_vertices_xyz;
        if (Array.isArray(poly) && poly.length >= 2) {
          const typesRaw = s?.polyline_vertex_types;
          const pts = poly
            .map((p) => (Array.isArray(p) && p.length >= 3 ? new THREE.Vector3(p[0], p[2], p[1]) : null))
            .filter(Boolean);
          if (pts.length < 2) return null;
          const types = Array.isArray(typesRaw) && typesRaw.length ? typesRaw : Array(pts.length).fill('main');
          return projectAndSegmentString({ points: pts, types }, roofPlanes, footprint);
        }

        const panels = s?.panels || [];
        const roofVertexIndices = (data?.verticesType || [])
          .map((t, idx) => (t === 'roof' ? idx : null))
          .filter((v) => v !== null);
        const pts = panels
          .map(([ri, pi]) => {
            const center = getPanelCenter(ri, pi);
            if (!center) return null;
            const point = center.clone ? center.clone() : center;
            const roofIdxMap = data?.stringingRoofIndexMap;
            const mappedRoofIdx = Array.isArray(roofIdxMap) ? roofIdxMap[ri] : ri;
            const vertexIdx = roofVertexIndices[mappedRoofIdx];
            if (vertexIdx !== undefined) point.roofPlaneHint = vertexIdx;
            return point;
          })
          .filter(Boolean);
        if (pts.length < 2) return null;
        return projectAndSegmentString({ points: pts, types: Array(pts.length).fill('main') }, roofPlanes, footprint);
      })
      .filter(Boolean);

    setStringLines(built);
  }, [
    showStringing,
    data?.stringingData,
    data?.verticesType,
    data?.vertices3D,
    data?.footprint,
    data?.footprintPolygon,
    data?.footprint_polygon,
    data?.lead?.property?.footprint,
    getPanelCenter,
    accordion
  ]);

  const [roofVertices, obstacleVertices, obstacleShapeTypes] = useMemo(() => {
    const roofs = vertices3D?.filter((s, si) => verticesType[si] === 'roof');
    const obs = vertices3D?.filter((s, si) => verticesType[si] === 'obstacle');
    const obsTypes = new Array(obstacles.length);
    obstacles.forEach((ob, obi) => {
      obsTypes[obi] = ob.shape;
      if (ob.shape === 'Point') {
        const geom = obs[obi];
        if (geom && geom.length > 0) {
          const cent = geom
            .reduce((acc, item) => acc.add(item), new THREE.Vector3(0, 0, 0))
            .multiplyScalar(1.0 / geom.length);
          obs[obi] = [cent, geom[0]];
        }
      }
    });
    return [roofs, obs, obsTypes];
  }, [vertices3D, verticesType, obstacles]);

  const treeVertices = useMemo(
    () => treeData.map((t) => [new THREE.Vector3(...t.position), new THREE.Vector3(...t.end)]),
    [trees, treeData]
  );
  const shapeTypes = useMemo(() => {
    // 🔧 FIX: Add bounds check to prevent accessing undefined segments/obstacles
    // This happens when vertices array hasn't updated yet after deletion (race condition)
    return vertices.map((_, index) => {
      if (index < segments.length) {
        const segment = segments[index];
        return segment?.shape || 'Polygon'; // Roof segments are always polygons
      } else {
        const obstacleIndex = index - segments.length;
        const obstacle = obstacles[obstacleIndex];
        return obstacle?.shape || 'Polygon';
      }
    });
  }, [segments, obstacles, vertices]);

  const drivewayVertices = useMemo(() => {
    return annotations && annotations.filter((a) => a.objectType && a.objectType === 'Driveway').map((a) => a.geometry);
  }, [annotations]);

  const drivewayText = useMemo(() => {
    return annotations && annotations.filter((a) => a.objectType && a.objectType === 'Driveway').map((a) => a.text);
  }, [annotations]);

  const wiringVertices = useMemo(() => {
    return annotations && annotations.filter((a) => a.objectType && a.objectType === 'Wiring').map((a) => a.geometry);
  }, [annotations]);

  const metersVertices = useMemo(() => {
    return annotations && annotations.filter((a) => a.objectType && a.objectType === 'Meter').map((a) => a.geometry);
  }, [annotations]);

  const getParentRoof = (i) => {
    const pi = parents[i];
    if (pi) return roofRefs.current[pi[0]];
    return null;
  };

  useImperativeHandle(forwardedRef, () => ({
    getCamera() {
      return controls.current.getCamera();
    }
  }));

  return (
    <DesignContext.Provider value={data}>
      <Sky />
      <Sun />
      <group name="roof" rotation={[Math.PI / 2.0, 0, 0]} castShadow receiveShadow visible={showRoofs}>
        {vertices
          .filter((poly) => poly !== null)
          .map((poly, index) => (
            <Roof
              ref={(el) => (roofRefs.current[index] = el)}
              key={index}
              index={index}
              type={verticesType[index]}
              uv={UV[index]}
              vertices={poly}
              vertices3D={vertices3D[index]}
              eave={eaves[index]}
              panelEave={panelEaves?.[index] ?? null}
              pitch={pitches[index]}
              azimuths={azimuths[index]}
              eaveHeight={eaveHeights[index]}
              isSelected={selectedObjects.roofSegments.includes(index)}
              selectable={
                (accordion === 'design' ? panelPlacementOnlySelectedRoof : segmentSelection || accordion === 'revise') &&
                verticesType[index] === 'roof' &&
                !readonly &&
                (mode === 'select' || (accordion === 'design' && panelPlacementOnlySelectedRoof))
              }
              panels={panelData[index] ?? null}
              setbacks={setbacksData[index] ?? null}
              setbacks3D={setbacks3dData[index] ?? null}
              shapeType={shapeTypes[index]}
              getParentRoof={getParentRoof}
              transformation={roofTransformations[index]}
            />
          ))}
      </group>
      <group name="floor" rotation={[Math.PI / 2.0, 0, 0]} castShadow receiveShadow visible={showRoofs}>
        {orgVertices.map(
          (poly, index) =>
            shapeTypes[index] != 'Line' && (
              <Roof
                key={index}
                uv={UV[index]}
                vertices={poly}
                eave={eaves[index]}
                eaveHeight={0}
                selectable={false}
                type="floor"
                shapeType={shapeTypes[index]}
                transformation={roofTransformations[index]}
              />
            )
        )}
      </group>
      <group name="points" rotation={[Math.PI / 2.0, 0, 0]} castShadow receiveShadow visible={showRoofs}>
        {pointSelection && !readonly
          ? vertices3D.map((poly, pIndex) =>
              verticesType[pIndex] === 'roof'
                ? poly.map((v, vIndex) => (
                    <Vertex
                      position={[v.x, v.y, -v.z]}
                      key={`${pIndex}-${vIndex}`}
                      index={[pIndex, vIndex]}
                      addSelectedPoint={addSelectedPoint}
                      removeSelectedPoint={removeSelectedPoint}
                      isSelected={selectedObjects.roofVertices[pIndex]?.includes(vIndex) ?? false}
                      selectable
                    />
                  ))
                : null
            )
          : null}
      </group>
      <group name="walls" rotation={[Math.PI / 2.0, 0, 0]} visible={showRoofs}>
        {vertices3D.map((poly, index) => poly && shapeTypes[index] != 'Line' && <Wall vertices={poly} />)}
      </group>
      <group name="trees" castShadow receiveShadow>
        {treeData.map((tree, treeIndex) => (
          <Tree
            position={tree.position}
            height={tree.height}
            radius={tree.radius}
            trunkHeight={tree.trunkHeight}
            treeType={tree.treeType}
            index={tree.index}
            key={tree.index}
            isSelected={false}
            selectable={false}
            visible={showTrees}
            onTreeClick={
              onTreeClick && accordion === 'trees' && (mode === 'select' || mode === 'delete')
                ? () => onTreeClick(treeIndex)
                : undefined
            }
          />
        ))}
      </group>
      <group name="ground" rotation={[Math.PI / 2.0, 0, 0]} castShadow receiveShadow>
        <Ground
          showSunLight
          showDSM
          position={[(cx / c) * scale, (cy / c) * scale, 0]}
          width={gwidth * scale}
          height={gheight * scale}
          visible={showGround && showSunLight && showDSM}
        />
        <Ground
          showSunLight
          showDSM={false}
          position={[(cx / c) * scale, (cy / c) * scale, 0]}
          width={gwidth * scale}
          height={gheight * scale}
          visible={showGround && showSunLight && !showDSM}
        />
        <Ground
          showSunLight={false}
          showDSM
          position={[(cx / c) * scale, (cy / c) * scale, 0]}
          width={gwidth * scale}
          height={gheight * scale}
          visible={showGround && !showSunLight && showDSM}
        />
        <Ground
          showSunLight={false}
          showDSM={false}
          position={[(cx / c) * scale, (cy / c) * scale, 0]}
          width={gwidth * scale}
          height={gheight * scale}
          visible={showGround && !showSunLight && !showDSM}
        />
      </group>
      <group name="panel_points" rotation={[Math.PI / 2.0, 0, 0]}>
        {false && panelData
          ? panelData.map((poly, pIndex) =>
              poly.points3D.map((v, vIndex) =>
                v.map((vt, vti) => (
                  <Vertex
                    position={[vt.x, vt.y, -vt.z]}
                    index={[pIndex, vIndex]}
                    addSelectedPoint={addSelectedPoint}
                    removeSelectedPoint={removeSelectedPoint}
                    selectable={false}
                    size={0.2}
                  />
                ))
              )
            )
          : null}
      </group>
      <group name="eaves" rotation={[Math.PI / 2.0, 0, 0]}>
        {false && eaves
          ? eaves.map(([a, b, type], pIndex) =>
              type ? (
                <>
                  <Vertex position={[a.x, a.y, -15 + -1 * pIndex]} selectable={false} size={0.2} color="red" />
                  <Vertex position={[b.x, b.y, -20 + -1 * pIndex]} selectable={false} size={0.2} color="blue" />
                </>
              ) : null
            )
          : null}
      </group>
      <ObjectSelection
        canvas={canvas}
        controls={controls}
        vertices={vertices3D.filter((v3, v3i) => verticesType[v3i] === 'roof')}
        panels={panelData}
        moveSelection={moveSelection}
        moveFunction={panelSelection ? movePanels : null}
        insertObject={insertObject}
        insertFunction={panelSelection ? insertPanels : null}
        canInsertObject={canInsertPanel}
        enabled
      />
      <Annotations canvas={canvas} controls={controls} enabled />
      <StringingDrawing strings={stringLines} visible={showStringing} />
      <CanvasShapeAnnotations canvas={canvas} controls={controls} enabled />
      <Drawing
        canvas={canvas}
        controls={controls}
        shapes={roofVertices}
        labels={labels}
        tool="Polygon"
        enabled={accordion === 'segments'}
        visible={(accordion === 'segments' || colorSetbacksByEdgeType || mode !== 'select') && showRoofs}
        colorEdgesByLabel={!!colorSetbacksByEdgeType}
      />
      <Drawing
        canvas={canvas}
        controls={controls}
        shapes={obstacleVertices}
        shapeTypes={obstacleShapeTypes}
        tool={tool}
        maxPoints={tool == 'Line' ? 2 : 1e3}
        enabled={accordion === 'obstacles'}
        visible={showRoofs}
        outlineLineObstaclesInSelect
      />
      <Drawing
        canvas={canvas}
        controls={controls}
        shapes={treeVertices}
        tool="Circle"
        enabled={accordion === 'trees'}
        visible={showTrees}
      />
      <Drawing
        canvas={canvas}
        controls={controls}
        shapes={drivewayVertices}
        tool="Polygon"
        objectType="Driveway"
        enabled={accordion === 'annotations' && (viewState == 'driveway' || annotationDeletePicking)}
        visible={accordion === 'annotations' && viewMode == '2D'}
        texts={drivewayText}
        backgroundColor="green"
        backgroundStyle="hashed"
        fontScale={2.0}
      />
      <Drawing
        canvas={canvas}
        controls={controls}
        shapes={wiringVertices}
        tool="Line"
        objectType="Wiring"
        enabled={accordion === 'annotations' && (viewState == 'wiring' || annotationDeletePicking)}
        visible={accordion === 'annotations' && viewMode == '2D'}
        dots={true}
        ringScale={0.4}
      />
      <Drawing
        canvas={canvas}
        controls={controls}
        shapes={metersVertices}
        tool="Line"
        objectType="Meter"
        maxPoints={2}
        enabled={accordion === 'annotations' && (viewState == 'meter' || annotationDeletePicking)}
        visible={accordion === 'annotations' && viewMode == '2D'}
        dots={true}
        lineColor="red"
        forceMeasurements={true}
        fontScale={2.0}
        hasSegments={true}
      />
      <GridHelper visible={viewMode === '3D'} />
      <Controls ref={controls} viewMode={viewMode} initialZoom={initialZoom} />
    </DesignContext.Provider>
  );
});

export default Scene;
